import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import {
  CreditCardIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

const Billing = () => {
  const { user, refreshUser } = useAuth();
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [purchasingExtras, setPurchasingExtras] = useState(false);
  const [extraAmount, setExtraAmount] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    loadPackages();
    loadPaymentHistory();
    
    // Check for payment success/cancelled URL params
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    
    if (success === 'true') {
      // Payment was successful - refresh user data and show success message
      toast.success('Payment successful! Your subscription has been activated.');
      refreshUser(); // Refresh user data to show updated billing info
      loadPaymentHistory(); // Reload payment history
      // Clear the URL params
      setSearchParams({});
    } else if (cancelled === 'true') {
      toast.error('Payment was cancelled.');
      // Clear the URL params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, refreshUser]);

  const loadPackages = async () => {
    try {
      const response = await api.get('/packages/active');
      if (response.data.success) {
        setPackages(response.data.packages);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await api.get('/payments/history');
      if (response.data.success) {
        setPaymentHistory(response.data.payments);
      }
    } catch (error) {
      console.error('Error loading payment history:', error);
      toast.error('Failed to load payment history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handlePurchasePackage = async (packageId) => {
    setSelectedPackage(packageId);
    setPurchasing(true);
    try {
      const response = await api.post('/payments/create-subscription', {
        packageId: packageId
        // No payCurrency - let NOWPayments payment page handle currency selection
      });

      if (response.data.success && response.data.paymentUrl) {
        // Redirect to payment page where user can select currency
        window.location.href = response.data.paymentUrl;
      } else {
        toast.error('Failed to create payment. Please try again.');
      }
    } catch (error) {
      console.error('Error purchasing package:', error);
      toast.error(error.response?.data?.error || 'Failed to create payment');
    } finally {
      setPurchasing(false);
      setSelectedPackage(null);
    }
  };

  const handlePurchaseExtras = async () => {
    if (!extraAmount || parseInt(extraAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setPurchasingExtras(true);
    try {
      const response = await api.post('/payments/purchase-extras', {
        amount: parseInt(extraAmount)
        // No payCurrency - let NOWPayments payment page handle currency selection
      });

      if (response.data.success && response.data.paymentUrl) {
        // Redirect to payment page where user can select currency
        window.location.href = response.data.paymentUrl;
      } else {
        toast.error('Failed to create payment. Please try again.');
      }
    } catch (error) {
      console.error('Error purchasing extras:', error);
      toast.error(error.response?.data?.error || 'Failed to create payment');
    } finally {
      setPurchasingExtras(false);
      setExtraAmount('');
    }
  };

  const getSubscriptionStatus = () => {
    const subscription = user?.billing?.subscription;
    if (!subscription || !subscription.status) {
      return { status: 'none', message: 'No active subscription' };
    }

    // Handle beta_tester and trial statuses (perpetual subscriptions)
    if (subscription.status === 'beta_tester' || subscription.status === 'trial') {
      return { 
        status: subscription.status, 
        message: subscription.status === 'beta_tester' ? 'Beta Tester (Active)' : 'Trial (Active)',
        packageTier: subscription.packageTier,
        endDate: null // No expiration
      };
    }

    if (subscription.status === 'active') {
      const endDate = new Date(subscription.endDate);
      const now = new Date();
      const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysLeft <= 0) {
        return { status: 'expired', message: 'Subscription expired' };
      }
      return { 
        status: 'active', 
        message: `Active - ${daysLeft} days remaining`,
        endDate: subscription.endDate,
        packageTier: subscription.packageTier
      };
    }

    return { status: subscription.status, message: `Subscription ${subscription.status}` };
  };

  const subscriptionStatus = getSubscriptionStatus();
  const remaining = user?.billing?.conversationsRemaining || 0;
  const monthlyAllowance = user?.billing?.monthlyAllowance || 0;
  const extraBalance = user?.billing?.extraConversationsBalance || 0;
  const pricePerExtra = user?.billing?.pricePerExtraConversation || 0.06;

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-2xl ${
        isDarkMode 
          ? 'bg-gradient-to-tr from-slate-900 via-blue-950 to-slate-900' 
          : 'bg-gradient-to-tr from-sky-50 via-blue-100 to-sky-100'
      }`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient orbs */}
          <div className={`absolute top-0 right-1/4 w-64 h-64 rounded-full blur-3xl ${
            isDarkMode ? 'bg-blue-600/15' : 'bg-blue-300/35'
          }`} />
          <div className={`absolute -bottom-16 left-0 w-72 h-72 rounded-full blur-3xl ${
            isDarkMode ? 'bg-sky-600/20' : 'bg-sky-300/40'
          }`} />
          <div className={`absolute top-1/3 -right-10 w-48 h-48 rounded-full blur-3xl ${
            isDarkMode ? 'bg-cyan-600/10' : 'bg-cyan-200/30'
          }`} />
          
          {/* Grid pattern overlay */}
          <div 
            className={`absolute inset-0 ${isDarkMode ? 'opacity-[0.03]' : 'opacity-[0.08]'}`}
            style={{
              backgroundImage: `linear-gradient(${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
          
          {/* Floating decorative shapes - money/card themed */}
          <svg className="absolute top-8 right-20 w-20 h-20 opacity-10" viewBox="0 0 100 100">
            <rect x="15" y="25" width="70" height="50" rx="8" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="2" />
            <line x1="15" y1="45" x2="85" y2="45" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="2" />
          </svg>
          <svg className="absolute bottom-10 left-16 w-14 h-14 opacity-10" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke={isDarkMode ? '#7dd3fc' : '#0284c7'} strokeWidth="2" />
            <text x="50" y="60" textAnchor="middle" fill={isDarkMode ? '#7dd3fc' : '#0284c7'} fontSize="30" fontWeight="bold">$</text>
          </svg>
          <svg className="absolute top-1/2 left-8 w-10 h-10 opacity-10" viewBox="0 0 100 100">
            <rect x="20" y="20" width="60" height="60" rx="6" fill="none" stroke={isDarkMode ? '#67e8f9' : '#0891b2'} strokeWidth="2" transform="rotate(15 50 50)" />
          </svg>
        </div>
        
        {/* Content */}
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Icon/Logo area */}
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-blue-500 to-sky-600 shadow-lg shadow-blue-500/30' 
                  : 'bg-gradient-to-br from-blue-500 to-sky-500 shadow-lg shadow-blue-400/40'
              }`}>
                <CreditCardIcon className="w-8 h-8 text-white" />
                <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-cyan-400' : 'bg-cyan-500'
                }`}>
                  <SparklesIcon className="w-3 h-3 text-white" />
                </div>
              </div>
              
              {/* Text content */}
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Billing & Subscription
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Manage your subscription and purchase additional conversations
                </p>
                <div className={`mt-3 flex items-center space-x-4 text-sm ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      subscriptionStatus.status === 'active' 
                        ? (isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500')
                        : (isDarkMode ? 'bg-slate-500' : 'bg-slate-400')
                    }`} />
                    {subscriptionStatus.message}
                  </span>
                  {remaining > 0 && (
                    <span className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        isDarkMode ? 'bg-blue-400' : 'bg-blue-500'
                      }`} />
                      {remaining} Remaining
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Status Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Current Status</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Conversations Remaining</span>
              {subscriptionStatus.status === 'active' && (
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              )}
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {remaining.toLocaleString()}
            </div>
            {monthlyAllowance > 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Monthly allowance: {monthlyAllowance.toLocaleString()}
                {extraBalance > 0 && ` • Extras: ${extraBalance.toLocaleString()}`}
              </div>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Subscription</span>
              {subscriptionStatus.status === 'active' ? (
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-red-500" />
              )}
            </div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {subscriptionStatus.message}
            </div>
            {subscriptionStatus.packageTier && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Package: {subscriptionStatus.packageTier}
              </div>
            )}
            {subscriptionStatus.endDate && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Expires: {new Date(subscriptionStatus.endDate).toLocaleDateString()}
              </div>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Price per Extra</span>
              <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
            </div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              ${pricePerExtra.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              per conversation
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Packages */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Subscription Packages</h2>
        
        {loading ? (
          <div className="text-center py-8 text-gray-600 dark:text-gray-400">Loading packages...</div>
        ) : packages.length === 0 ? (
          <div className="text-center py-8 text-gray-600 dark:text-gray-400">No packages available</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {packages.map((pkg) => {
              const isCurrentPackage = subscriptionStatus.packageTier === pkg.packageId;
              const isSelected = selectedPackage === pkg.packageId;
              
              return (
                <div
                  key={pkg.packageId}
                  className={`border-2 rounded-2xl p-6 ${
                    isCurrentPackage
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {pkg.name}
                    </h3>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                      ${pkg.price}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">per month</div>
                    
                    <div className="space-y-2 text-sm text-left mb-4">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Accounts:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {pkg.maxAccounts.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Conversations:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {pkg.monthlyConversations.toLocaleString()}/mo
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Extra price:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          ${pkg.pricePerExtra.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {pkg.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        {pkg.description}
                      </p>
                    )}

                    <button
                      onClick={() => handlePurchasePackage(pkg.packageId)}
                      disabled={purchasing || isCurrentPackage}
                      className={`w-full px-4 py-2 rounded-xl font-medium transition-colors ${
                        isCurrentPackage
                          ? 'bg-green-600 text-white cursor-not-allowed'
                          : purchasing && isSelected
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-primary-600 hover:bg-primary-700 text-white'
                      }`}
                    >
                      {isCurrentPackage
                        ? 'Current Package'
                        : purchasing && isSelected
                        ? 'Processing...'
                        : 'Subscribe'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Purchase Extra Conversations */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Purchase Extra Conversations
        </h2>
        
        <div className="max-w-md">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Buy additional conversations that will be added to your balance. These conversations persist even after subscription renewal.
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Number of Conversations
              </label>
              <input
                type="number"
                value={extraAmount}
                onChange={(e) => setExtraAmount(e.target.value)}
                min="1"
                placeholder="Enter amount"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {extraAmount && parseInt(extraAmount) > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600 dark:text-gray-400">Conversations:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {parseInt(extraAmount).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600 dark:text-gray-400">Price per conversation:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    ${pricePerExtra.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-gray-300 dark:border-gray-600 pt-2 mt-2">
                  <span className="text-gray-900 dark:text-white">Total:</span>
                  <span className="text-primary-600 dark:text-primary-400">
                    ${(parseInt(extraAmount) * pricePerExtra).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <button
              onClick={handlePurchaseExtras}
              disabled={purchasingExtras || !extraAmount || parseInt(extraAmount) <= 0}
              className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
            >
              {purchasingExtras ? 'Processing...' : 'Purchase Extra Conversations'}
            </button>
          </div>
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Payment History</h2>
        
        {loadingHistory ? (
          <div className="text-center py-8 text-gray-600 dark:text-gray-400">Loading payment history...</div>
        ) : paymentHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-600 dark:text-gray-400">No payment history found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {paymentHistory.map((payment) => {
                  const isSuccessful = ['confirmed', 'finished'].includes(payment.status);
                  const isPending = ['pending', 'waiting', 'confirming'].includes(payment.status);
                  const isFailed = ['failed', 'expired', 'refunded'].includes(payment.status);
                  
                  return (
                    <tr key={payment._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {new Date(payment.createdAt).toLocaleDateString()} {new Date(payment.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                          {payment.type === 'subscription' ? 'Subscription' : 'Extras'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                        {payment.description || (payment.type === 'subscription' 
                          ? `${payment.packageName || payment.packageTier} Package`
                          : `${payment.extrasAmount} extra conversations`)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        ${payment.amount.toFixed(2)} {payment.currency}
                        {payment.payAmount && payment.payCurrency && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            ({payment.payAmount} {payment.payCurrency})
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isSuccessful && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 flex items-center">
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            Success
                          </span>
                        )}
                        {isPending && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200">
                            Pending
                          </span>
                        )}
                        {isFailed && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 flex items-center">
                            <XCircleIcon className="h-4 w-4 mr-1" />
                            {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-4">
        <div className="flex items-start">
          <CreditCardIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
              Payment Information
            </h3>
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Payments are processed securely via cryptocurrency. After payment confirmation, your subscription will be activated automatically.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Billing;

